import { useMemo } from 'react';
import { ApolloError, useReactiveVar } from '@apollo/client';

import {
  StudentSlackWorkspacesQuery,
  useStudentSlackWorkspacesQuery,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { MANAGEMENT_ROLES } from '../Palette/groupCardRoles';

/** Строка «Slack группы студента» (#54) — ровно то, что отдаёт сервер. */
export type IStudentSlackWorkspace = NonNullable<
  StudentSlackWorkspacesQuery['student']
>['slackWorkspaces'][number];

export interface IUseStudentSlackWorkspaces {
  /** Роль видит Slack студента (`erp_management_viewer` бэкенда). */
  canSee: boolean;
  workspaces: IStudentSlackWorkspace[];
  /** По id группы — строка группы в карточке ищет свою. */
  byGroupId: Map<string, IStudentSlackWorkspace>;
  loading: boolean;
  error?: ApolloError;
}

// Стабильные пустышки: новый массив на каждый рендер в зависимостях эффекта
// дал бы вечный цикл.
const EMPTY: IStudentSlackWorkspace[] = [];

/**
 * #54 — Slack групп студента: ссылки и ручная деактивация.
 *
 * Гейт — `MANAGEMENT_ROLES`, тот же, что у поля на бэкенде
 * (`StudentType.slackWorkspaces` → `erp_management_viewer`). Чужой роли
 * сервер отдал бы пустой список, но запрос мы и не шлём: экран не должен
 * спрашивать то, что роли не положено.
 *
 * Прав бота это не трогает (решение владельца 07.10.2026): деактивирует
 * человек в админке workspace, ERP только даёт ссылку.
 */
export const useStudentSlackWorkspaces = (
  studentId?: string | null,
): IUseStudentSlackWorkspaces => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  const canSee = roles.some((role) => MANAGEMENT_ROLES.includes(role));

  const { data, loading, error } = useStudentSlackWorkspacesQuery({
    variables: { id: studentId || '' },
    skip: !studentId || !canSee,
    fetchPolicy: 'cache-and-network',
  });

  const workspaces = data?.student?.slackWorkspaces ?? EMPTY;
  const byGroupId = useMemo(
    () => new Map(workspaces.map((row) => [row.groupId, row])),
    [workspaces],
  );

  return {
    canSee,
    workspaces: canSee ? workspaces : EMPTY,
    byGroupId,
    loading: canSee && loading,
    error: canSee ? error : undefined,
  };
};
